import { __awaiter } from "tslib";
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { ReducerType } from '../types';
import { MISSING_CUSTOMER } from '@open-tender/types';
import { checkAuth, selectToken } from './account';
import { adjustGroupOrder, makeCartPayload, resetGroupOrder } from '../groupOrder';
import { addMessage, resetAlert, setAlert, setCart, setRequestedAt, setRevenueCenter } from '../order';
import { makeRequestedAtStr, makeSimpleCart, rehydrateCart, timezoneMap } from '@open-tender/utils';
import { showNotification } from '../notifications';
const initialState = {
    entities: [],
    lookup: {},
    loading: 'idle',
    error: null
};
export var CustomerGroupOrdersActionType;
(function (CustomerGroupOrdersActionType) {
    CustomerGroupOrdersActionType["FetchCustomerGroupOrders"] = "customer/fetchCustomerGroupOrders";
    CustomerGroupOrdersActionType["FetchCustomerGroupOrder"] = "customer/fetchCustomerGroupOrder";
    CustomerGroupOrdersActionType["RemoveCustomerGroupOrder"] = "customer/removeCustomerGroupOrder";
    CustomerGroupOrdersActionType["ReopenGroupOrder"] = "customer/reopenGroupOrder";
    CustomerGroupOrdersActionType["UpdateCustomerGroupOrder"] = "customer/updateCustomerGroupOrder";
    CustomerGroupOrdersActionType["CloseCustomerGroupOrder"] = "customer/closeCustomerGroupOrder";
})(CustomerGroupOrdersActionType || (CustomerGroupOrdersActionType = {}));
export const fetchCustomerGroupOrders = createAsyncThunk(CustomerGroupOrdersActionType.FetchCustomerGroupOrders, (_, { dispatch, getState, rejectWithValue }) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        const api = getState().config.api;
        const token = selectToken(getState());
        if (!token)
            throw new Error(MISSING_CUSTOMER);
        const orders = yield api.getCustomerGroupOrders(token);
        return orders;
    }
    catch (err) {
        const error = err;
        return checkAuth(error, dispatch, () => rejectWithValue(error));
    }
}));
export const fetchCustomerGroupOrder = createAsyncThunk(CustomerGroupOrdersActionType.FetchCustomerGroupOrder, (cartId, { dispatch, getState, rejectWithValue }) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        const api = getState().config.api;
        const token = selectToken(getState());
        if (!token)
            throw new Error(MISSING_CUSTOMER);
        const response = yield api.getCustomerGroupOrder(token, cartId);
        const payload = Object.assign(Object.assign({}, makeCartPayload(response)), { isCartOwner: true });
        return payload;
    }
    catch (err) {
        const error = err;
        return checkAuth(error, dispatch, () => rejectWithValue(error));
    }
}));
export const makeCartData = (order, spendingLimit) => {
    const { address, revenueCenter, requestedAt, serviceType, cart } = order;
    if (!revenueCenter)
        return null;
    const orderData = {
        address,
        revenue_center_id: revenueCenter === null || revenueCenter === void 0 ? void 0 : revenueCenter.revenue_center_id,
        service_type: serviceType,
        requested_at: requestedAt,
        cart: cart ? makeSimpleCart(cart) : []
    };
    return spendingLimit
        ? Object.assign(Object.assign({}, orderData), { spending_limit: spendingLimit }) : orderData;
};
export const updateCustomerGroupOrder = createAsyncThunk(CustomerGroupOrdersActionType.UpdateCustomerGroupOrder, ({ cartId, callback }, { dispatch, getState, rejectWithValue }) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        const api = getState().config.api;
        const token = selectToken(getState());
        if (!token)
            throw new Error(MISSING_CUSTOMER);
        const cartData = makeCartData(getState().order);
        yield api.putCustomerGroupOrder(token, cartId, cartData);
        const response = yield api.getCustomerGroupOrder(token, cartId);
        const { requestedAt, revenueCenter } = getState().order;
        if (response.requested_at !== requestedAt) {
            dispatch(setRequestedAt(response.requested_at));
            if (revenueCenter) {
                const tz = timezoneMap[revenueCenter.timezone];
                const requestedAtText = makeRequestedAtStr(response.requested_at, tz);
                dispatch(addMessage(`Requested time updated to ${requestedAtText}`));
            }
        }
        const payload = Object.assign(Object.assign({}, makeCartPayload(response)), { isCartOwner: true });
        dispatch(adjustGroupOrder(payload));
        if (callback)
            callback();
    }
    catch (err) {
        const error = err;
        return checkAuth(error, dispatch, () => rejectWithValue(error));
    }
}));
export const closeGroupOrder = createAsyncThunk(CustomerGroupOrdersActionType.CloseCustomerGroupOrder, (requestData, { dispatch, getState, rejectWithValue }) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        const api = getState().config.api;
        const token = selectToken(getState());
        if (!token)
            throw new Error(MISSING_CUSTOMER);
        yield api.putCustomerGroupOrderStatus(token, requestData.cartId, {
            closed
        });
        return requestData.closed;
    }
    catch (err) {
        const error = err;
        return checkAuth(error, dispatch, () => rejectWithValue(error));
    }
}));
export const removeCustomerGroupOrder = createAsyncThunk(CustomerGroupOrdersActionType.RemoveCustomerGroupOrder, (requestData, { dispatch, getState, rejectWithValue }) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        const api = getState().config.api;
        const token = selectToken(getState());
        if (!token)
            throw new Error(MISSING_CUSTOMER);
        yield api.deleteCustomerGroupOrder(token, requestData.cartId);
        dispatch(resetGroupOrder());
        const orders = yield api.getCustomerGroupOrders(token);
        dispatch(showNotification('Group order deleted!'));
        if (requestData.callback)
            requestData.callback();
        return orders;
    }
    catch (err) {
        dispatch(resetGroupOrder());
        dispatch(showNotification('Group order deleted!'));
        const error = err;
        return checkAuth(error, dispatch, () => rejectWithValue(error));
    }
}));
export const reopenGroupOrder = createAsyncThunk(CustomerGroupOrdersActionType.ReopenGroupOrder, (cart, { dispatch, getState, rejectWithValue }) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        const api = getState().config.api;
        const token = selectToken(getState());
        if (!token)
            throw new Error(MISSING_CUSTOMER);
        const alert = {
            type: 'working',
            args: { text: 'Building your order...' }
        };
        dispatch(setAlert(alert));
        const { revenue_center_id, service_type, requested_at, cart_id } = cart;
        const revenueCenter = yield api.getLocation(revenue_center_id);
        dispatch(setRevenueCenter(revenueCenter));
        const menuItems = yield api.getMenuItems(revenue_center_id, service_type);
        const data = { revenue_center_id, requested_at, service_type };
        const response = yield api.putCustomerGroupOrder(token, cart_id, data);
        dispatch(setRequestedAt(response.requested_at));
        const customer = getState().customer.account.profile;
        const { customer_id } = customer || {};
        const items = response.cart.filter((i) => i.customer_id === customer_id);
        const { cart: customerCart } = rehydrateCart(menuItems, items);
        dispatch(setCart(customerCart));
        const payload = Object.assign(Object.assign({}, makeCartPayload(response)), { isCartOwner: true, cartOwner: customer });
        dispatch(adjustGroupOrder(payload));
        dispatch(showNotification('Group order reopened!'));
        dispatch(setAlert({ type: 'groupOrder' }));
    }
    catch (err) {
        dispatch(resetAlert());
        dispatch(addMessage('Something went wrong. Please contact support.'));
        const error = err;
        return checkAuth(error, dispatch, () => rejectWithValue(error));
    }
}));
const customerGroupOrdersSlice = createSlice({
    name: ReducerType.GroupOrders,
    initialState,
    reducers: {
        resetCustomerGroupOrders: () => initialState,
        resetCustomerGroupOrdersError: state => {
            state.error = null;
            state.loading = 'idle';
        },
        setCustomerGroupOrders: (state, action) => {
            state.entities = action.payload;
            state.error = null;
        }
    },
    extraReducers: builder => {
        builder
            .addCase(fetchCustomerGroupOrders.fulfilled, (state, action) => {
            state.entities = action.payload;
            state.loading = 'idle';
            state.error = null;
        })
            .addCase(fetchCustomerGroupOrders.pending, state => {
            state.loading = 'pending';
        })
            .addCase(fetchCustomerGroupOrders.rejected, (state, action) => {
            state.error = action.payload;
            state.loading = 'idle';
        })
            .addCase(fetchCustomerGroupOrder.fulfilled, (state, action) => {
            state.entities = [action.payload];
            state.loading = 'idle';
            state.error = null;
        })
            .addCase(fetchCustomerGroupOrder.pending, state => {
            state.loading = 'pending';
        })
            .addCase(fetchCustomerGroupOrder.rejected, (state, action) => {
            state.error = action.payload;
            state.loading = 'idle';
        })
            .addCase(removeCustomerGroupOrder.fulfilled, (state, action) => {
            state.entities = action.payload;
            state.loading = 'idle';
            state.error = null;
        })
            .addCase(removeCustomerGroupOrder.pending, state => {
            state.loading = 'pending';
        })
            .addCase(removeCustomerGroupOrder.rejected, (state, action) => {
            state.error = action.payload;
            state.loading = 'idle';
        })
            .addCase(reopenGroupOrder.fulfilled, state => {
            state.error = null;
            state.loading = 'idle';
        })
            .addCase(reopenGroupOrder.pending, state => {
            state.loading = 'pending';
        })
            .addCase(reopenGroupOrder.rejected, (state, action) => {
            state.error = action.payload;
            state.loading = 'idle';
        })
            .addCase(updateCustomerGroupOrder.fulfilled, state => {
            state.error = null;
            state.loading = 'idle';
        })
            .addCase(updateCustomerGroupOrder.pending, state => {
            state.loading = 'pending';
        })
            .addCase(updateCustomerGroupOrder.rejected, (state, action) => {
            state.error = action.payload;
            state.loading = 'idle';
        })
            .addCase(closeGroupOrder.fulfilled, (state, action) => {
            return Object.assign(Object.assign({}, state), { closed: action.payload, loading: 'idle', error: null });
        })
            .addCase(closeGroupOrder.pending, state => {
            state.loading = 'pending';
        })
            .addCase(closeGroupOrder.rejected, (state, action) => {
            state.error = action.payload;
            state.loading = 'idle';
        });
    }
});
export const { resetCustomerGroupOrders, resetCustomerGroupOrdersError, setCustomerGroupOrders } = customerGroupOrdersSlice.actions;
export const selectCustomerGroupOrders = (state) => state.customer.groupOrders;
export const customerGroupOrdersReducer = customerGroupOrdersSlice.reducer;
